import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { hasSupabase } from '@/lib/env'
import Shell from '@/components/layout/Shell'
import Home from '@/pages/Home'
import NowPage from '@/pages/now/Now'
import MapPage from '@/pages/map/MapPage'
import SearchPage from '@/pages/search/Search'
import PostPage from '@/pages/post/Post'
import SavedPage from '@/pages/saved/Saved'
import InterestsPage from '@/pages/interests/Interests'
import FollowingPage from '@/pages/following/Following'
import ProfilePage from '@/pages/profile/Profile'
import SettingsPage from '@/pages/settings/Settings'
import PrivacyPage from '@/pages/privacy/Privacy'
import HelpCenterPage from '@/pages/help/HelpCenter'
import LoginPage from '@/pages/Login'
import BizProfile from '@/pages/biz/BizProfile'
import BizDashboard from '@/pages/biz/Dashboard'
import AdminDashboard from '@/pages/admin/Dashboard'
import ToastContainer from '@/components/ui/Toast'

/**
 * Sin Supabase configurado (modo diseño/dev) → todas las rutas accesibles.
 * Con Supabase → protección real por rol.
 */
function RequireAuth({ children, role }: { children: React.ReactNode; role?: string }) {
  const { user, profile, loading } = useAuth()

  // Sin Supabase configurado → acceso libre (modo diseño)
  if (!hasSupabase) return <>{children}</>

  if (loading) return <div style={{ height: '100vh', background: '#F1F3F2' }} />
  if (!user) return <Navigate to="/login" replace />

  // Verificar rol del perfil real en Supabase
  if (role === 'business' && profile?.role !== 'business') return <Navigate to="/" replace />
  if (role === 'admin' && profile?.role !== 'admin') return <Navigate to="/" replace />

  return <>{children}</>
}

export default function App() {
  return (
    <>
      <Routes>
        {/* Login */}
        <Route path="/login" element={<LoginPage />} />

        {/* App de cliente */}
        <Route element={<Shell />}>
          <Route path="/" element={<Home />} />
          <Route path="/now" element={<NowPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/post/:id" element={<PostPage />} />
          <Route path="/biz/:id" element={<BizProfile />} />
          <Route
            path="/saved"
            element={
              <RequireAuth>
                <SavedPage />
              </RequireAuth>
            }
          />
          <Route
            path="/interests"
            element={
              <RequireAuth>
                <InterestsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/following"
            element={
              <RequireAuth>
                <FollowingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/privacy"
            element={
              <RequireAuth>
                <PrivacyPage />
              </RequireAuth>
            }
          />
          <Route path="/help" element={<HelpCenterPage />} />
        </Route>

        {/* Dashboard negocio */}
        <Route
          path="/dashboard/*"
          element={
            <RequireAuth role="business">
              <BizDashboard />
            </RequireAuth>
          }
        />

        {/* Panel admin */}
        <Route
          path="/admin/*"
          element={
            <RequireAuth role="admin">
              <AdminDashboard />
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <ToastContainer />
    </>
  )
}
